import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Eye, EyeOff, Trash2, Plus, Loader2, ExternalLink, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCMACompsQuery, useCMACompsMutation, type CMAComp } from "@/hooks/useCMAData";
import { cn } from "@/lib/utils";

interface CMACompsManagerProps {
  cmaId: string;
  zpid?: string | null;
  address?: string | null;
}

const MAX_VISIBLE = 5;

const STATUS_OPTIONS = ["FOR_SALE", "PENDING", "SOLD", "FOR_RENT"];
const STATUS_LABELS: Record<string, string> = {
  FOR_SALE: "Active",
  PENDING: "Pending",
  SOLD: "Sold",
  FOR_RENT: "For Rent",
};

function statusBadge(status: string | null) {
  const s = status || "UNKNOWN";
  if (s === "SOLD" || s.includes("SOLD"))
    return <Badge variant="secondary" className="text-xs">{s}</Badge>;
  if (s === "PENDING")
    return <Badge className="bg-yellow-500 text-white text-xs">Pending</Badge>;
  if (s === "FOR_SALE")
    return <Badge className="bg-green-600 text-white text-xs">Active</Badge>;
  return <Badge variant="outline" className="text-xs">{s}</Badge>;
}

const EMPTY_COMP: Omit<CMAComp, "id" | "cma_id" | "created_at"> = {
  address: "",
  city: "",
  state: "",
  zip: "",
  price: null,
  bedrooms: null,
  bathrooms: null,
  square_feet: null,
  lot_size: null,
  price_per_sqft: null,
  property_type: null,
  status: "FOR_SALE",
  days_on_market: null,
  zillow_url: null,
  image_url: null,
  visible: true,
};

export function CMACompsManager({ cmaId, zpid, address }: CMACompsManagerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: comps = [], isLoading } = useCMACompsQuery(cmaId);
  const { addComp, updateComp, deleteComp } = useCMACompsMutation(cmaId);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [form, setForm] = useState(EMPTY_COMP);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const visibleCount = comps.filter((c) => c.visible).length;

  const handleToggleVisibility = async (comp: CMAComp) => {
    if (!comp.visible && visibleCount >= MAX_VISIBLE) {
      toast({
        title: `Max ${MAX_VISIBLE} comps visible`,
        description: "Hide another comp first.",
        variant: "destructive",
      });
      return;
    }
    try {
      await updateComp.mutateAsync({ id: comp.id, updates: { visible: !comp.visible } });
    } catch {
      toast({ title: "Error", description: "Failed to update comp", variant: "destructive" });
    }
  };

  const handleDelete = async (compId: string) => {
    try {
      await deleteComp.mutateAsync(compId);
      toast({ title: "Comp removed" });
    } catch {
      toast({ title: "Error", description: "Failed to delete comp", variant: "destructive" });
    }
  };

  const handleAdd = async () => {
    if (!form.address) {
      toast({ title: "Address is required", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const price = form.price || null;
      const sqft = form.square_feet || null;
      const ppsq = price && sqft ? Math.round(price / sqft) : null;
      const isVisible = visibleCount < MAX_VISIBLE;
      await addComp.mutateAsync({ ...form, price_per_sqft: ppsq, visible: isVisible });
      toast({ title: "Comp added" });
      setIsAddOpen(false);
      setForm(EMPTY_COMP);
    } catch {
      toast({ title: "Error", description: "Failed to add comp", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefetchComps = async () => {
    if (!zpid && !address) {
      toast({ title: "No address or ZPID available to refetch comps", variant: "destructive" });
      return;
    }
    setIsRefetching(true);
    try {
      const { data, error } = await supabase.functions.invoke("fetch-cma-comps", {
        body: { zpid, address, cmaId },
      });
      if (error) throw error;
      toast({ title: "Comps refreshed", description: `${data?.count || 0} properties found` });
      queryClient.invalidateQueries({ queryKey: ["cma-comps", cmaId] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to refresh comps";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setIsRefetching(false);
    }
  };

  const soldComps = comps.filter((c) => c.status === "SOLD" || c.status?.includes("SOLD"));
  const activeComps = comps.filter((c) => c.status !== "SOLD" && !c.status?.includes("SOLD"));

  function CompsTable({ items, label }: { items: CMAComp[]; label: string }) {
    if (items.length === 0) return null;
    return (
      <div>
        <h4 className="text-sm font-medium text-muted-foreground mb-2">{label}</h4>
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Address</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-center hidden md:table-cell">Beds/Baths</TableHead>
                <TableHead className="text-right hidden md:table-cell">Sqft</TableHead>
                <TableHead className="text-right hidden lg:table-cell">$/Sqft</TableHead>
                <TableHead className="text-center hidden lg:table-cell">DOM</TableHead>
                <TableHead className="text-center">Visible</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((comp) => (
                <TableRow key={comp.id} className={cn(!comp.visible && "opacity-50")}>
                  <TableCell className="max-w-[180px]">
                    <div>
                      <p className="text-sm font-medium truncate">{comp.address}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {[comp.city, comp.state, comp.zip].filter(Boolean).join(", ")}
                      </p>
                      {comp.zillow_url && (
                        <a
                          href={comp.zillow_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-500 hover:underline flex items-center gap-1"
                        >
                          Zillow <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium">
                    {comp.price ? `$${comp.price.toLocaleString()}` : "—"}
                  </TableCell>
                  <TableCell className="text-center text-sm hidden md:table-cell">
                    {comp.bedrooms ?? "—"}/{comp.bathrooms ?? "—"}
                  </TableCell>
                  <TableCell className="text-right text-sm hidden md:table-cell">
                    {comp.square_feet ? comp.square_feet.toLocaleString() : "—"}
                  </TableCell>
                  <TableCell className="text-right text-sm hidden lg:table-cell">
                    {comp.price_per_sqft ? `$${comp.price_per_sqft}` : "—"}
                  </TableCell>
                  <TableCell className="text-center text-sm hidden lg:table-cell">
                    {comp.days_on_market ?? "—"}
                  </TableCell>
                  <TableCell className="text-center">
                    <button
                      onClick={() => handleToggleVisibility(comp)}
                      title={comp.visible ? "Hide from CMA" : "Show on CMA"}
                      className="p-1 rounded hover:bg-muted transition-colors"
                    >
                      {comp.visible ? (
                        <Eye className="h-4 w-4 text-green-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => handleDelete(comp.id)}
                      title="Remove comp"
                      className="p-1 rounded hover:bg-muted transition-colors"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            {comps.length} comps total · {visibleCount}/{MAX_VISIBLE} visible on report
          </p>
        </div>
        <div className="flex gap-2">
          {zpid && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefetchComps}
              disabled={isRefetching}
              className="gap-2"
            >
              {isRefetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh Comps
            </Button>
          )}
          <Button size="sm" onClick={() => setIsAddOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Comp
          </Button>
        </div>
      </div>

      {/* Tables */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : comps.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground border border-dashed rounded-lg">
          <p className="text-sm">No comps yet. Add manually or refresh from Zillow.</p>
        </div>
      ) : (
        <>
          <CompsTable items={activeComps} label="Active / Pending" />
          <CompsTable items={soldComps} label="Sold" />
        </>
      )}

      {/* Add Comp Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Add Comparable Property</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2">
              <Label>Address *</Label>
              <Input
                value={form.address || ""}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="123 Main St"
              />
            </div>
            <div>
              <Label>City</Label>
              <Input
                value={form.city || ""}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                placeholder="City"
              />
            </div>
            <div>
              <Label>State</Label>
              <Input
                value={form.state || ""}
                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                placeholder="ST"
                maxLength={2}
              />
            </div>
            <div>
              <Label>Zip</Label>
              <Input
                value={form.zip || ""}
                onChange={(e) => setForm((f) => ({ ...f, zip: e.target.value }))}
                placeholder="12345"
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select
                value={form.status || "FOR_SALE"}
                onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>List/Sale Price ($)</Label>
              <Input
                type="number"
                value={form.price || ""}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value ? Number(e.target.value) : null }))}
                placeholder="450000"
              />
            </div>
            <div>
              <Label>Bedrooms</Label>
              <Input
                type="number"
                step="0.5"
                value={form.bedrooms || ""}
                onChange={(e) => setForm((f) => ({ ...f, bedrooms: e.target.value ? Number(e.target.value) : null }))}
                placeholder="3"
              />
            </div>
            <div>
              <Label>Bathrooms</Label>
              <Input
                type="number"
                step="0.5"
                value={form.bathrooms || ""}
                onChange={(e) => setForm((f) => ({ ...f, bathrooms: e.target.value ? Number(e.target.value) : null }))}
                placeholder="2"
              />
            </div>
            <div>
              <Label>Square Feet</Label>
              <Input
                type="number"
                value={form.square_feet || ""}
                onChange={(e) => setForm((f) => ({ ...f, square_feet: e.target.value ? Number(e.target.value) : null }))}
                placeholder="1800"
              />
            </div>
            <div>
              <Label>Days on Market</Label>
              <Input
                type="number"
                value={form.days_on_market || ""}
                onChange={(e) => setForm((f) => ({ ...f, days_on_market: e.target.value ? Number(e.target.value) : null }))}
                placeholder="21"
              />
            </div>
            <div className="col-span-2">
              <Label>Zillow URL (optional)</Label>
              <Input
                value={form.zillow_url || ""}
                onChange={(e) => setForm((f) => ({ ...f, zillow_url: e.target.value || null }))}
                placeholder="https://www.zillow.com/homedetails/..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={isSubmitting} className="gap-2">
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Add Comp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
